import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { IntlProvider } from "react-intl";
import { Helmet } from "react-helmet";
import GoogleFontLoader from "react-google-font-loader";
import Modal from "./components/Modal";
import standardTheme from "./theme";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localeDataPlugin from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import each from "lodash/each";
import { Route, Switch } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import DotLoader from "./components/Loaders/DotLoader";
import LoginSAMLConsume from "./components/LoginSAMLConsume";
import LoginEpsilonConsume from "./components/LoginEpsilonConsume";
import LoginIdpHubConsume from "./components/LoginIDPHubConsume";
import IdleTimeout from "./components/IdleTimeout";
import TokenRefresher from "./components/TokenRefresher";
import OfflineOverlay from "../../components3/src/OfflineOverlay";
import { Toaster } from "react-hot-toast";
import {
  loadLocale3,
  loadPublicConfiguration3,
  confirmWarning,
  clearResumeTicket,
  setAppModalContent,
} from "./actions";
import {
  getInitApp3Success,
  getConfiguration3,
  getLocale3,
  getLocale3Data,
  getError,
  getWarning,
  getUser,
  getResumeTicket,
  getPageTitle,
  getIsOffline,
  getModalContent,
  getForm3Data,
} from "./reducers";
import ViewRoot from "./components/ViewRoot";
import { runEffect3 } from "./actions/effect3";
import CloseTab from "components3/src/CloseTab";
import qs from "qs";
import useDidMount from "components3/src/common3/hooks/useDidMount";

function App(props) {
  const {
    isAppInit = false,
    configuration = {},
    localeData = {},
    loadLocale,
    loadConfiguration,
    error,
    warning,
    user,
    confirmWarning,
    resumeTicket = {},
    clearResumeTicket,
    modalContent,
    clearModalContent,
    pageTitle,
    runEffect,
    isOffline,
  } = props;
  const { locale = "sv", messages = {}, localeData: dateLocale } = localeData;
  const queryParams = qs.parse(window?.location?.search, {
    ignoreQueryPrefix: true,
  });
  const { timedOut = false } = queryParams || {};
  const [_timedOut, setTimedOut] = useState(timedOut);
  const [title, setTitle] = useState(pageTitle || configuration?.title || "");
  const didMount = useDidMount();

  useEffect(() => {
    if (didMount) {
      dayjs.locale(locale, dateLocale);

      dayjs.extend(relativeTime);
      dayjs.extend(localeDataPlugin);
      dayjs.extend(localizedFormat);
    }
  }, [locale, dateLocale, dayjs]);

  useEffect(() => {
    if (isAppInit === true) {
      loadLocale(null);
      loadConfiguration();
    }
  }, [isAppInit, loadLocale, loadConfiguration]);

  useEffect(() => {
    let titleToReturn;

    if (!pageTitle) {
      if (configuration?.title) {
        titleToReturn = configuration.title;
      } else {
        const host = window.location.host;
        const path =
          window.location.pathname !== "/" ? window.location.pathname : "";
        titleToReturn = `${host}${path}`;
      }
    } else {
      titleToReturn = pageTitle;
    }

    setTitle(titleToReturn);
  }, [pageTitle, configuration]);

  const { theme = null, enableOfflineOverlay = false } = configuration || {};

  // eslint-disable-next-line
  const fontsArray = [];

  // eslint-disable-next-line
  const subsetsArray = [];

  useMemo(() => {
    // TODO: All fonts are treated as google fonts. Only load google fonts
    each(theme, (value) => {
      const { fontFamily, subsets = null } = value;

      if (fontFamily) {
        fontsArray.push({
          font: fontFamily,
          weights: [400, "400", "700"],
        });
        subsetsArray.push(subsets);
      }
    });
  }, [theme, subsetsArray, fontsArray]);

  const GlobalStyle = createGlobalStyle`
    body {
      ${(p) =>
        p.theme &&
        p.theme.primary &&
        p.theme.primary.fontFamily &&
        `font-family: ${p.theme.primary.fontFamily};`}
      ${(p) =>
        p.theme &&
        p.theme.primary &&
        p.theme.primary.fontSize &&
        `font-size: ${p.theme.primary.fontSize}${p.theme.primary.fontSizeUnit};`}
      ${(p) =>
        p.theme &&
        p.theme.primary &&
        p.theme.primary.lineHeight &&
        `line-height: ${p.theme.primary.lineHeight}${p.theme.primary.lineHeightUnit};`}
      ${(p) =>
        p.theme &&
        p.theme.primary &&
        p.theme.primary.color &&
        `color: ${p.theme.primary.color};`}
    }
  `;

  if (!configuration) {
    return null;
  }

  if (!isAppInit) {
    return null;
  }

  const deleteTicket = (ticketId) => {
    runEffect({
      source: "Ticket/delete",
      context: { selected: [ticketId], dangerously: true },
      pageType: "formTab",
    });
  };

  const redirectToTicket = () => {
    const { currentFormDataId, formDataId } = resumeTicket;
    const url = new URL(window.location.href);

    // Delete the open blank ticket
    deleteTicket(currentFormDataId);

    // Redirect to active form
    url.searchParams.set("formDataId", formDataId);
    window.location.href = url.href;
  };

  const startNewTicket = () => {
    const { removeActiveTicket = false, formDataId } = resumeTicket;

    if (removeActiveTicket) {
      deleteTicket(formDataId);
    }

    clearResumeTicket();
  };

  const parseElementString = (elementString) => {
    if (!elementString) {
      return;
    }

    // Add closing brackets for self-closing tags
    elementString = elementString.replace(/<(\w+)([^>]*?)\/?>/g, "<$1$2></$1>");

    const parser = new DOMParser();
    const doc = parser.parseFromString(
      "<html><body>" + elementString + "</body></html>",
      "text/html"
    );
    const element = doc.body.firstChild;

    if (!element) {
      return null;
    }

    const tagName = element.tagName.toLowerCase();
    const props = {};

    for (let i = 0; i < element.attributes.length; i++) {
      const attribute = element.attributes[i];
      props[attribute.name] = attribute.value;
    }

    return {
      element: tagName,
      props: props,
    };
  };

  const _helmetToComponent = (str) => {
    // stop if str is empty
    if (!str.length) {
      return;
    }

    // an array of React components
    let Components = [];

    str.split(/\n/).forEach((node) => {
      let match = parseElementString(node);
      if (match) {
        Components.push(React.createElement(match.element, match.props));
      }
    });

    // return the array of components
    return Components;
  };

  let meta = _helmetToComponent(configuration.meta || "");

  const closeTimedOutModal = () => {
    const currentQueryParams = new URLSearchParams(window.location.search);
    if (currentQueryParams?.has("timedOut")) {
      // Remove query param
      currentQueryParams.delete("timedOut");
      window.history.replaceState(
        null,
        "",
        "?" + currentQueryParams?.toString()
      );
      setTimedOut(false);
    }
  };

  return (
    <IntlProvider
      locale={locale}
      messages={messages}
      onError={(err) => {
        if (err.code === "MISSING_TRANSLATION") {
          return;
        }
        console.error(err);
      }}
    >
      <ErrorBoundary>
        <React.Suspense fallback={<DotLoader />}>
          <Helmet>
            <title>{title}</title>
            {configuration.favicon && (
              <link
                rel="icon"
                type="image/png"
                href={`/a/${configuration.favicon}`}
                sizes="16x16"
              />
            )}
            {meta}
          </Helmet>
          {!!fontsArray.length && (
            <GoogleFontLoader fonts={fontsArray} subsets={subsetsArray} />
          )}
          <ThemeProvider theme={{ ...standardTheme, ...theme }}>
            <Toaster position="bottom-right" />
            <GlobalStyle />
            {/* This timeout is only triggered for logged in users.
            If you are looking for the one triggered for anonymous users,
            it is located in Form3. */}
            {user && (
              <IdleTimeout
                idleWarning={configuration.idleWarning}
                idleLogout={configuration.idleLogout}
              />
            )}
            <TokenRefresher idleLogout={configuration.idleLogout} />
            <Switch>
              <Route path={`/loginSAMLConsume`} component={LoginSAMLConsume} />
              <Route
                path={`/loginEpsilonConsume`}
                component={LoginEpsilonConsume}
              />
              <Route
                path={`/login-idphub-consume`}
                component={LoginIdpHubConsume}
              />
              <Route path={`/closeTab`} component={CloseTab} />
              <Route exact path={`/`} component={ViewRoot} />
              <Route exact path={`/:url`} component={ViewRoot} />
              <Route exact path={`/:url/:id`} component={ViewRoot} />
              <Route path={`*`} component={ViewRoot} />
            </Switch>
            {error && <Modal heading="Error" text={error} homeButton />}
            {enableOfflineOverlay && isOffline && <OfflineOverlay />}
            {warning && (
              <Modal
                type="warning"
                heading="Warning"
                text={warning}
                confirm={() => confirmWarning()}
              />
            )}
            {resumeTicket.formDataId && (
              <Modal
                type="warning"
                heading="Modal.activeTicketHeading"
                text="Modal.activeTicketText"
                confirm={redirectToTicket}
                close={startNewTicket}
                confirmLabel="Modal.activeTicketConfirm"
                closeLabel="Modal.activeTicketClose"
              />
            )}
            {_timedOut && (
              <Modal
                type="warning"
                heading="Modal.timedOutHeading"
                text="Modal.timedOutText"
                closeLabel="Close"
                close={closeTimedOutModal}
              />
            )}
            {modalContent && (
              <Modal
                heading={modalContent.heading}
                text={modalContent.content}
                flexDirection="row-reverse"
                confirm={
                  modalContent.confirmButton
                    ? () => clearModalContent("confirm", props)
                    : false
                }
                confirmLabel={modalContent.confirmLabel}
                closeLabel={modalContent.closeLabel}
                extraCloseButton={modalContent.extraCloseButton}
                type={modalContent.type}
                close={() => clearModalContent("close", props)}
              />
            )}
          </ThemeProvider>
        </React.Suspense>
      </ErrorBoundary>
    </IntlProvider>
  );
}

const mapStateToProps = (state) => {
  return {
    isAppInit: getInitApp3Success(state),
    locale: getLocale3(state),
    localeData: getLocale3Data(state),
    configuration: getConfiguration3(state),
    error: getError(state),
    warning: getWarning(state),
    user: getUser(state),
    resumeTicket: getResumeTicket(state),
    pageTitle: getPageTitle(state),
    isOffline: getIsOffline(state),
    modalContent: getModalContent(state),
    formData: getForm3Data(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loadLocale: (locale) => {
      dispatch(loadLocale3(locale));
    },
    loadConfiguration: () => {
      dispatch(loadPublicConfiguration3({}));
    },
    confirmWarning: () => {
      dispatch(confirmWarning());
    },
    clearResumeTicket: () => {
      dispatch(clearResumeTicket());
    },
    clearModalContent: (type, props) => {
      const { modalContent } = props;
      switch (type) {
        case "confirm":
          if (modalContent.confirmEffect) {
            props.runEffect({
              source: modalContent.confirmEffect,
              ...props,
              pageId: "AppModalContent",
              pageType: "savedEffect",
              componentId: "AppModalContent",
              context: {
                confirm: true,
                formDataId: props.formData?._id,
              },
            });
          } else {
            dispatch(setAppModalContent(null));
          }
          break;
        case "close":
          if (modalContent.cancelEffect) {
            props.runEffect({
              source: modalContent.cancelEffect,
              ...props,
              pageId: "AppModalContent",
              pageType: "savedEffect",
              componentId: "AppModalContent",
              context: {
                close: true,
                formDataId: props.formData?._id,
              },
            });
          } else {
            dispatch(setAppModalContent(null));
          }
          break;
      }
    },
    runEffect: ({
      source,
      effectId,
      props,
      pageId,
      pageType,
      componentId,
      context,
    }) => {
      dispatch(
        runEffect3({
          source,
          effectId,
          props,
          pageId,
          pageType,
          componentId,
          context,
        })
      );
    },
  };
};

// eslint-disable-next-line
App = connect(mapStateToProps, mapDispatchToProps)(App);

export default App;
